import { useEffect, useRef, useState } from "react";
import { apiRequest } from "../../services/api";
export default function SisCancelExecution({
  company,
  saleId,
  requestId,
  accessToken,
  canExecute,
  onCancelled,
}) {
  const [preview, setPreview] = useState(null),
    [checks, setChecks] = useState({}),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const generation = useRef(0),
    lock = useRef(false),
    key = useRef(null),
    base = `/accounting/${company}/supplier-liabilities/sis/pos/${saleId}/correction-requests/${requestId}`;
  useEffect(() => {
    generation.current++;
    setPreview(null);
    setChecks({});
    setError("");
    setBusy(false);
    lock.current = false;
    key.current = null;
    return () => {
      generation.current++;
    };
  }, [base, accessToken]);
  async function run(execute) {
    if (lock.current) return;
    const ticket = generation.current;
    lock.current = true;
    setBusy(true);
    setError("");
    try {
      let body;
      if (execute) {
        if (!canExecute || !preview || !checks.a || !checks.b || !checks.c)
          throw Error("Review and confirm actual cancellation first");
        if (!window.crypto?.randomUUID)
          throw Error("Secure UUID support required");
        if (!key.current) key.current = window.crypto.randomUUID();
        body = {
          date: preview.snapshot.date,
          previewHash: preview.previewHash,
          requestKey: key.current,
          liabilityCancellationReviewed: true,
          noGoodsReturnConfirmed: true,
          noPaymentOrRefundConfirmed: true,
        };
      } else {
        setPreview(null);
        setChecks({});
        key.current = null;
        body = {
          date: new Date(Date.now() + 19800000).toISOString().slice(0, 10),
        };
      }
      const r = await apiRequest(
        `${base}/${execute ? "cancel" : "cancel-preview"}`,
        { method: "POST", accessToken, body },
      );
      if (ticket === generation.current) {
        if (execute) {
          setPreview(null);
          onCancelled(r);
        } else setPreview(r);
      }
    } catch (e) {
      if (ticket === generation.current)
        setError(
          e.message ||
            "Unable to review/execute cancellation. Unchanged retries reuse the original key.",
        );
    } finally {
      if (ticket === generation.current) {
        setBusy(false);
        lock.current = false;
      }
    }
  }
  return (
    <section className="mt-3 space-y-3 border-t pt-3">
      <p className="text-sm">
        Dependency-free cancel-only execution. Requires a fresh request dated
        today, the same open original year and no allocation history. Corrected
        reposting is not included.
      </p>
      <button
        type="button"
        className="rounded border px-3 py-2 disabled:opacity-40"
        disabled={busy || !canExecute}
        onClick={() => run(false)}
      >
        Preview actual accrual cancellation
      </button>
      {error && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}
      {preview && (
        <div className="space-y-3">
          <p>{preview.warning}</p>
          <p className="font-semibold">
            Cancel original liability ₹{preview.snapshot.total} on{" "}
            {preview.snapshot.date}
          </p>
          <ul>
            {(preview.displayLines || preview.snapshot.lines).map((l, i) => (
              <li key={i}>
                {l.ledgerCode
                  ? `${l.ledgerCode} · ${l.ledgerName}`
                  : `Ledger ${l.ledgerId}`}{" "}
                · Dr ₹{l.debit} · Cr ₹{l.credit}
              </li>
            ))}
          </ul>
          {[
            "I reviewed the exact supplier liability and expense cancellation amounts.",
            "No goods were returned; this must not create stock or POS return records.",
            "No payment or refund is being made, and no replacement accrual will be posted.",
          ].map((label, i) => (
            <label key={label} className="flex gap-2 text-sm">
              <input
                type="checkbox"
                disabled={busy}
                checked={!!checks["abc"[i]]}
                onChange={(e) =>
                  setChecks((x) => ({ ...x, ["abc"[i]]: e.target.checked }))
                }
              />
              {label}
            </label>
          ))}
          <button
            type="button"
            className="rounded bg-red-800 px-4 py-2 text-white disabled:opacity-40"
            disabled={
              busy || !canExecute || !checks.a || !checks.b || !checks.c
            }
            onClick={() => run(true)}
          >
            Post cancel-only reversal
          </button>
        </div>
      )}
    </section>
  );
}
